import debounce from 'lodash/debounce'
import isEqual from 'lodash/isEqual'
import React, { useEffect, useMemo, useState } from 'react'

import { PLAYLIST } from '@disco/constants/ItemTypes'
import { Trans } from '@disco/i18n/trans'
import { useTranslation } from '@disco/i18n/use-translation'
import {
  clientLibraryFeaturedPlaylistsSet,
  clientLibraryFeaturedPlaylistsUpdate,
  clientLibraryPlaylistsLoad,
  clientLibraryPlaylistsLoadNext,
  clientLibraryPlaylistsUpdate,
} from '@disco/state-management/actions/client-library-playlist-actions'
import { getClientLibraryPlaylists } from '@disco/state-management/selectors/client-libraries'

import type { ClientLibraryId } from 'types/client-library'
import type { Playlist, PlaylistId } from 'types/playlist'
import type { State } from 'types/redux'
import type { PopoverItemType } from 'types/ui'

import emptyArtists1x from 'assets/images/empty-artists.png'
import emptyArtists2x from 'assets/images/empty-artists@2x.png'
import emptySearch1x from 'assets/images/empty-search.png'
import emptySearch2x from 'assets/images/empty-search@2x.png'

import PlaylistGridItem from 'components/common/playlist-grid-item'
import Row from 'components/common/row'
import { Tab, Tabs } from 'components/ui'
import AspectRatio from 'components/ui/aspect-ratio'
import Draggable from 'components/ui/draggable'
import EllipsisMenu from 'components/ui/ellipsis-menu'
import EmptyState from 'components/ui/empty-state'
import Grid from 'components/ui/grid'
import GridItem from 'components/ui/grid-item'
import InfiniteScroll from 'components/ui/infinite-scroll'
import InlineLink from 'components/ui/inline-link'
import Input from 'components/ui/input'
import Sortable from 'components/ui/sortable'

import { useDispatch, useSelector } from 'hooks/use-redux'

type Props = {
  libraryId: ClientLibraryId
  basePath: string
  contentClassName?: string
}

/**
 * Published and featured playlists of a catalog: publishing, featuring, reordering
 * featured playlists and linking to their add and edit modals.
 * @param libraryId - ID of the catalog
 * @param basePath - Path the add (`/add`) and edit (`/:playlistId`) modal routes are nested under
 * @param contentClassName - Class for the element wrapping each tab's playlists, e.g. to make it scroll
 */
const LibraryPlaylistsManager = ({
  libraryId,
  basePath,
  contentClassName,
}: Props): React.ReactNode => {
  const { t } = useTranslation('catalogs-management')
  const dispatch = useDispatch()
  const playlists = useSelector((state: State) =>
    getClientLibraryPlaylists(state, libraryId),
  )
  const [selectedTab, setSelectedTab] = useState<number>(0)
  const [query, setQuery] = useState<string>('')
  const [isLoadingNext, setIsLoadingNext] = useState<boolean>(false)
  const debouncedQuery = useMemo(
    () =>
      debounce(query => {
        dispatch(clientLibraryPlaylistsLoad(libraryId, query))
      }, 300),
    [dispatch, libraryId],
  )
  useEffect(() => () => debouncedQuery.cancel(), [debouncedQuery])
  useEffect(() => {
    // A search still waiting to fire would overwrite the list loaded here
    debouncedQuery.cancel()

    if (selectedTab === 0) {
      dispatch(clientLibraryPlaylistsLoad(libraryId, query))
    }

    if (selectedTab === 1) {
      setQuery('')
      dispatch(clientLibraryPlaylistsLoad(libraryId, '', true))
    }

    setIsLoadingNext(false)
  }, [selectedTab, libraryId, dispatch, debouncedQuery])

  const updateSearchQuery = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoadingNext(false)
    setQuery(event.target.value)
    debouncedQuery(event.target.value)
  }

  const toggleIsFeatured = (playlist: Playlist) => () => {
    let add = []
    let remove = []

    if (playlist['is_featured']) {
      remove.push(playlist['playlist_id'])
    } else {
      add.push(playlist['playlist_id'])
    }

    dispatch(clientLibraryFeaturedPlaylistsUpdate(libraryId, add, remove))
  }

  const removePlaylistFromLibrary = (playlistId: PlaylistId) => () => {
    dispatch(clientLibraryPlaylistsUpdate(libraryId, [], [playlistId]))
  }

  const setFeaturedPlaylists = item => {
    const order: PlaylistId[] = item?.order.map(Number)

    if (!isEqual(playlists?.list, order)) {
      dispatch(clientLibraryFeaturedPlaylistsSet(libraryId, order))
    }
  }

  const loadNext = (next: string) => {
    setIsLoadingNext(true)
    dispatch(clientLibraryPlaylistsLoadNext(libraryId, next))
  }

  const getEllipsisMenuOptions = (playlist: Playlist): PopoverItemType[] => [
    {
      label: playlist['is_featured']
        ? t(
            'catalogs-management:removeFromFeaturedPlaylists',
            'Remove from featured playlists',
          )
        : t(
            'catalogs-management:addToFeaturedPlaylists',
            'Add to featured playlists',
          ),
      onClick: toggleIsFeatured(playlist),
    },
    {
      label: t('catalogs-management:editPlaylist', 'Edit playlist'),
      link: {
        url: `${basePath}/${playlist['playlist_id']}`,
      },
      type: 'router-link',
    },
    {
      label: t(
        'catalogs-management:unpublishThisPlaylist',
        'Unpublish this playlist',
      ),
      onClick: removePlaylistFromLibrary(playlist['playlist_id']),
    },
  ]

  const renderContent = (playlists: {
    loading: boolean
    loaded: boolean
    nextPage: string | null
    items: Record<PlaylistId, Playlist>
    list: PlaylistId[]
  }) => (
    <div className={contentClassName}>
      {!isLoadingNext && playlists.loading
        ? t('catalogs-management:loading', 'Loading...')
        : renderPlaylists(playlists)}
    </div>
  )

  const renderPlaylists = (playlists: {
    loading: boolean
    loaded: boolean
    nextPage: string | null
    items: Record<PlaylistId, Playlist>
    list: PlaylistId[]
  }) => {
    const items: Playlist[] = playlists.list.map(
      (id: PlaylistId) => playlists.items[id],
    )
    return playlists.loaded && playlists.list.length === 0 ? (
      renderEmptyState()
    ) : (
      <InfiniteScroll
        next={playlists.nextPage}
        loadNext={loadNext}
        loadingNode={t('catalogs-management:loading', 'Loading...')}
      >
        {selectedTab === 0 && (
          <Grid size="large">
            {renderAddItem()}
            {items.map(renderItem)}
          </Grid>
        )}
        {selectedTab === 1 && (
          <Sortable
            accept={[PLAYLIST]}
            id={`${libraryId}-featured-playlists`}
            items={items}
            keyExtractor={(item: Playlist) => String(item['playlist_id'])}
            renderItem={renderItem}
            onDrop={setFeaturedPlaylists}
          >
            {items => <Grid size="large">{items}</Grid>}
          </Sortable>
        )}
      </InfiniteScroll>
    )
  }

  const renderAddItem = (): React.ReactNode => (
    <GridItem
      link={{
        href: `${basePath}/add`,
      }}
      media={
        <AspectRatio fill="#5f63ee" ratio={3 / 2}>
          <svg width="27" height="26" viewBox="0 0 27 26">
            <path
              fill="#fff"
              d="M24,14.8333333 L14.8333333,14.8333333 L14.8333333,24 C14.8333333,25.0083333 14.0083333,25.8333333 13,25.8333333 C11.9916667,25.8333333 11.1666667,25.0083333 11.1666667,24 L11.1666667,14.8333333 L2,14.8333333 C0.991666667,14.8333333 0.166666667,14.0083333 0.166666667,13 C0.166666667,11.9916667 0.991666667,11.1666667 2,11.1666667 L11.1666667,11.1666667 L11.1666667,2 C11.1666667,0.991666667 11.9916667,0.166666667 13,0.166666667 C14.0083333,0.166666667 14.8333333,0.991666667 14.8333333,2 L14.8333333,11.1666667 L24,11.1666667 C25.0083333,11.1666667 25.8333333,11.9916667 25.8333333,13 C25.8333333,14.0083333 25.0083333,14.8333333 24,14.8333333 Z"
              transform="translate(.5)"
            />
          </svg>
        </AspectRatio>
      }
      title={t(
        'catalogs-management:publishNewPlaylist',
        'Publish new playlist',
      )}
    />
  )

  const renderItem = (playlist: Playlist): React.ReactNode => {
    if (!playlist) return null
    const item: React.ReactNode = (
      <PlaylistGridItem
        key={playlist['playlist_id']}
        playlist={playlist}
        status={
          playlist['is_featured']
            ? t('catalogs-management:featured', 'Featured')
            : undefined
        }
        overlayActions={
          <EllipsisMenu
            compact
            appearance="black"
            options={getEllipsisMenuOptions(playlist)}
            icon="ellipsis"
            hasArrow={false}
          />
        }
      />
    )
    return selectedTab === 0 ? (
      item
    ) : (
      <Draggable
        key={playlist['playlist_id']}
        id={playlist['playlist_id']}
        type={PLAYLIST}
      >
        {item}
      </Draggable>
    )
  }

  const renderEmptyState = () => {
    if (query !== '') {
      return (
        <EmptyState>
          <img
            srcSet={`${emptySearch1x}, ${emptySearch2x} 2x`}
            width="177"
            height="160"
          />
          <p>
            <Trans t={t} i18nKey="catalogs-management:noPlaylistsFoundAddHere">
              Sorry! We couldn’t find anything for that search. Please try
              another query, or, if the playlist you’re looking for isn’t
              published yet,{' '}
              <InlineLink to={`${basePath}/add`}>add it here</InlineLink>.
            </Trans>
          </p>
        </EmptyState>
      )
    }

    if (selectedTab === 0) {
      return <Grid size="large">{renderAddItem()}</Grid>
    }

    if (selectedTab === 1) {
      return (
        <EmptyState>
          <img
            srcSet={`${emptyArtists1x}, ${emptyArtists2x} 2x`}
            width="194"
            height="185"
          />
          <p>
            {t(
              'catalogs-management:noFeaturedPlaylists',
              'No featured playlists here yet! You can feature playlists you’ve added to your Catalog in the “Published playlists” tab.',
            )}
          </p>
        </EmptyState>
      )
    }

    return null
  }

  return (
    <Tabs
      className="Tabs--modal"
      index={selectedTab}
      onChange={(index: number) => setSelectedTab(index)}
    >
      <Tab
        label={t(
          'catalogs-management:publishedPlaylists',
          'Published playlists',
        )}
      >
        <Row columnsSize={[3]}>
          <div>
            <Input
              icon="search"
              placeholder={t('catalogs-management:search', 'Search')}
              value={query}
              onChange={updateSearchQuery}
            />
          </div>
        </Row>
        {playlists && renderContent(playlists)}
      </Tab>
      <Tab
        label={t('catalogs-management:featuredPlaylists', 'Featured playlists')}
      >
        {playlists && renderContent(playlists)}
      </Tab>
    </Tabs>
  )
}

export default LibraryPlaylistsManager
